<template>
  <v-expansion-panel :value="connection.id">
    <v-expansion-panel-title>
      <div class="row-head">
        <div class="row-times font-mono">
          <span class="time">{{ time(connection.departure) }}</span>
          <v-icon icon="mdi-arrow-right" size="x-small" class="mx-1" />
          <span class="time">{{ time(connection.arrival) }}</span>
        </div>
        <div class="row-meta text-caption text-medium-emphasis">
          {{ duration }} · {{ transfersText }}
        </div>
        <div class="row-trains">
          <template v-for="(leg, index) in vehicleLegs" :key="index">
            <span v-if="index > 0" class="text-medium-emphasis" aria-hidden="true">·</span>
            <span class="train-label">
              <!-- TrainLink already lifts its own anchor above the panel-title overlay;
                   @click.stop is what keeps the click from also toggling the panel. -->
              <TrainLink v-if="leg.trainNumber" :trainNumber="leg.trainNumber" :category="leg.category"
                         @click.stop />
              <template v-else>{{ leg.trainLabel }}</template>
            </span>
          </template>
        </div>
        <div class="row-rating">
          <ConnectionRating :rating="connection.rating" kind="connection" />
          <div v-if="delayText" class="text-caption text-medium-emphasis">{{ delayText }}</div>
        </div>
      </div>
    </v-expansion-panel-title>

    <v-expansion-panel-text>
      <div v-for="(row, index) in rows" :key="index">
        <div v-if="row.transfer" class="transfer">
          <v-icon icon="mdi-swap-vertical" size="small" class="mr-2" />
          <div class="transfer-body">
            <div class="text-body-2">
              Umstieg {{ row.transfer.name }} ·
              {{ row.transfer.plannedMinutes }} min<template
                v-if="row.transfer.samePlatform"> · gleicher Bahnsteig</template>
            </div>
            <ConnectionRating :rating="row.transfer.rating" kind="transfer" />
            <div v-if="transferMetrics(row.transfer.rating)" class="metrics text-caption text-medium-emphasis">
              {{ transferMetrics(row.transfer.rating) }}
            </div>
          </div>
        </div>

        <div class="leg" :class="{ 'text-medium-emphasis': row.leg.kind === 'WALK' }">
          <v-icon :icon="row.leg.kind === 'WALK' ? 'mdi-walk' : 'mdi-train'" size="small" class="mr-2" />
          <div class="leg-body">
            <div class="text-body-2">
              <strong>{{ row.leg.trainLabel || (row.leg.kind === 'WALK' ? 'Fußweg' : '') }}</strong>
              <span class="font-mono ml-2">
                {{ time(row.leg.from) }} {{ row.leg.from.name }} →
                {{ time(row.leg.to) }} {{ row.leg.to.name }}
              </span>
            </div>
            <div v-if="cancellationText(row.leg)" class="text-caption text-error">
              {{ cancellationText(row.leg) }}
            </div>
            <ConnectionRating v-if="row.leg.kind !== 'WALK'" :rating="row.leg.rating" kind="leg" />
            <div v-if="legMetrics(row.leg.rating)" class="metrics text-caption text-medium-emphasis">
              {{ legMetrics(row.leg.rating) }}
            </div>
            <div v-if="legCancellations(row.leg.rating)" class="metrics text-caption text-medium-emphasis">
              {{ legCancellations(row.leg.rating) }}
            </div>
          </div>
        </div>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script>
import ConnectionRating from '@/components/ConnectionRating.vue'
import DateTimeMixin from '@/mixins/DateTimeMixin'
import TrainLink from '@/components/TrainLink.vue'

export default {
  name: 'ConnectionRow',

  components: { ConnectionRating, TrainLink },

  mixins: [DateTimeMixin],

  props: {
    connection: { type: Object, required: true },
  },

  computed: {
    vehicleLegs() {
      return this.connection.legs.filter(leg => leg.kind !== 'WALK');
    },

    /**
     * One entry per leg, carrying the transfer the traveller makes to reach it.
     *
     * Paired by ordinal, exactly the way the backend derives them (JourneyMapper.toTransferPoints:
     * transfer n spans vehicle leg n and vehicle leg n + 1), because the leg indices it computes
     * there are @JsonIgnore'd and never reach us — matching on them silently produced no transfer
     * block at all. Matching on evaId instead looks like the obvious alternative and is wrong: the
     * transfer's evaId comes from the *arriving* leg, so a footpath that changes station makes it
     * differ from the following leg's from.evaId, i.e. it would fail on precisely the shape that
     * motivated handling footpaths in the first place.
     */
    rows() {
      var transfers = this.connection.transferPoints || [];
      var vehicleLegsSeen = 0;
      return this.connection.legs.map(leg => {
        if (leg.kind === 'WALK') {
          return { leg: leg, transfer: null };
        }
        var transfer = vehicleLegsSeen > 0 ? transfers[vehicleLegsSeen - 1] || null : null;
        vehicleLegsSeen += 1;
        return { leg: leg, transfer: transfer };
      });
    },

    /**
     * The arrival delay, which the payload has always carried and no view ever showed. P80 and the
     * worst day rather than a mean: delay distributions are long-tailed, so an average describes
     * neither the typical day nor the one that actually ruins a trip. Cancellations are counted
     * separately because they have no delay in minutes at all.
     */
    delayText() {
      var rating = this.connection.rating;
      if (!rating || rating.arrivalDelayP80 == null) {
        return null;
      }
      var parts = [`an 4 von 5 Tagen höchstens ${rating.arrivalDelayP80} min später`];
      if (rating.arrivalDelayMax != null && rating.arrivalDelayMax > rating.arrivalDelayP80) {
        parts.push(`schlimmstenfalls ${rating.arrivalDelayMax} min`);
      }
      if (rating.cancelledDays) {
        parts.push(`${rating.cancelledDays} Ausfalltage`);
      }
      return `Ankunft: ${parts.join(' · ')}`;
    },

    duration() {
      var minutes = Math.round((this.connection.durationSeconds || 0) / 60);
      var hours = Math.floor(minutes / 60);
      return hours > 0 ? `${hours}:${String(minutes % 60).padStart(2, '0')} h` : `${minutes} min`;
    },

    transfersText() {
      var count = this.connection.transfers || 0;
      if (count === 0) {
        return 'direkt';
      }
      return count === 1 ? '1 Umstieg' : `${count} Umstiege`;
    },
  },

  methods: {
    /**
     * What "cancelled" actually means for this leg on the day being searched — which is not the
     * same as "this train does not run". bahn.de cancels the two ends separately, and a train
     * whose boarding stop is dropped is usually still running, just not from where you wanted to
     * get on. The old blanket "Ausfall gemeldet" said the strongest of those three things over
     * all of them.
     *
     * Says nothing about the past: how often this leg was cancelled is the metrics line below,
     * counted from our own history.
     */
    cancellationText: function(leg) {
      if (!leg.cancelled) {
        return null;
      }
      if (leg.originCancelled && leg.destinationCancelled) {
        return 'Fällt an diesem Tag aus';
      }
      if (leg.originCancelled) {
        return `Halt in ${leg.from.name} entfällt an diesem Tag`;
      }
      if (leg.destinationCancelled) {
        return `Halt in ${leg.to.name} entfällt an diesem Tag`;
      }
      // `cancelled` without either flag cannot happen the way the backend builds it, but an older
      // payload has no flags at all — and then the blanket wording is still the honest one.
      return 'Ausfall gemeldet';
    },

    /**
     * What the percentage beside it means in minutes, for one leg. Deliberately only minutes and
     * cancelled days: the rate, its wording and its day count are ConnectionRating's job, and
     * repeating them here would put the same sentence twice under every leg.
     *
     * Every figure is null-checked on its own — NON_NULL serialisation drops absent fields
     * entirely, and a leg cancelled on all its days carries a day count but no measurements.
     */
    legMetrics: function(rating) {
      if (!rating || rating.coverage === 'NONE') {
        return null;
      }
      var parts = [];
      if (rating.medianDelay != null) {
        parts.push(`typisch ${rating.medianDelay} min`);
      }
      if (rating.p80Delay != null) {
        parts.push(`P80 ${rating.p80Delay} min`);
      }
      // One span rather than two figures: "von -2 bis 63 min" reads as a range, where "min -2 ·
      // max 63" reads as two unrelated numbers.
      if (rating.minDelay != null && rating.maxDelay != null) {
        parts.push(`von ${rating.minDelay} bis ${rating.maxDelay} min`);
      }
      return parts.length ? `Verspätung: ${parts.join(' · ')}` : null;
    },

    // Kept apart from the delay figures on purpose: a cancellation has no delay in minutes, so
    // folding it into that line would invite reading it as one.
    legCancellations: function(rating) {
      if (!rating || rating.coverage === 'NONE' || !rating.cancelledRate) {
        return null;
      }
      // The multiplication LegRating's javadoc blesses, over the denominator it reports.
      var days = Math.round(rating.cancelledRate * rating.sampleDays);
      return days ? `Ausfall an ${days} von ${rating.sampleDays} Tagen` : null;
    },

    /**
     * The transfer's own minutes. `worstRealMinutes` may be negative — the connecting train had
     * already left — which is worth spelling out rather than leaving as a bare minus sign.
     */
    transferMetrics: function(rating) {
      if (!rating || rating.coverage === 'NONE') {
        return null;
      }
      var parts = [];
      if (rating.medianRealMinutes != null) {
        parts.push(`typisch ${rating.medianRealMinutes} min`);
      }
      if (rating.worstRealMinutes != null) {
        parts.push(`knappster ${rating.worstRealMinutes} min`);
      }
      if (!parts.length) {
        return null;
      }
      var text = `Übergang: ${parts.join(' · ')}`;
      if (rating.worstRealMinutes != null && rating.worstRealMinutes < 0) {
        text += ' — der Anschluss war schon weg';
      }
      return text;
    },

    // Realtime stamp when there is one, else the plan. toTime comes from DateTimeMixin,
    // which already handles the offset-bearing ISO strings the API sends.
    time: function(point) {
      if (!point) {
        return '';
      }
      return this.toTime(point.real || point.planned) || '';
    },
  },
};
</script>

<style scoped>
.row-head {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  width: 100%;
}
.row-times .time {
  font-size: 1.05rem;
  font-weight: 600;
}
.row-trains {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.row-rating {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.leg,
.transfer {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
}
.transfer {
  border-left: 2px dashed rgba(0, 0, 0, 0.2);
  margin-left: 7px;
  padding-left: 12px;
}
/* Wraps rather than clipping: these lines are long, and the detail panel is the one place
   with room for them. */
.metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.leg-body,
.transfer-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
